import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Play, PlayCircle, Pause, StepForward, RotateCcw, Square } from 'lucide-react';
import { useRunControl } from '@/context/run-control';

export default function RunToolbar({ flowId }: { flowId?: string | null }) {
  const { start, startInteractive, step, pause, resume, stop, runId, status, isBusy, mode } = useRunControl();

  const canStart = !runId || (status === 'completed' || status === 'failed' || status === 'canceled');
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isTerminal = status === 'completed' || status === 'failed' || status === 'canceled';
  const hasRun = Boolean(runId);

  const startRegular = React.useCallback(() => { if (flowId) void start(flowId); }, [flowId, start]);
  const startInter = React.useCallback(() => { if (flowId) void startInteractive(flowId); }, [flowId, startInteractive]);

  // Simple elapsed: measure from when runId is set until terminal
  const [t0, setT0] = React.useState<number | null>(null); // start time
  const [t1, setT1] = React.useState<number | null>(null); // stop time (terminal)
  const [now, setNow] = React.useState<number>(Date.now());
  // Start time when a run appears; clear stop time
  React.useEffect(() => {
    if (runId && !t0) {
      setT0(Date.now());
      setT1(null);
    }
    if (!runId) {
      setT0(null);
      setT1(null);
    }
  }, [runId, t0]);
  // Freeze timer on terminal by setting stop time once
  React.useEffect(() => {
    if (t0 && isTerminal && !t1) {
      setT1(Date.now());
    }
    if (isRunning && t1) {
      // if run resumes (shouldn't for terminal), clear stop time
      setT1(null);
    }
  }, [isTerminal, isRunning, t0, t1]);
  // Tick only while not terminal
  React.useEffect(() => {
    if (!t0 || isTerminal) return;
    const h = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(h);
  }, [t0, isTerminal]);
  const elapsedMs = t0 ? Math.max(0, (t1 ?? now) - t0) : 0;
  const fmt = (ms: number) => (ms < 1000 ? `${ms}ms` : `${Math.round(ms / 1000)}s`);

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={startRegular} disabled={!canStart || isBusy}><Play className="h-4 w-4 mr-1" />Run</Button>
      <Button size="sm" variant="outline" onClick={startInter} disabled={!canStart || isBusy}><PlayCircle className="h-4 w-4 mr-1" />Interactive</Button>
      {mode === 'interactive' && (
        <>
          <Separator orientation="vertical" className="h-5" />
          <Button size="sm" variant="outline" onClick={() => void step()} disabled={!isPaused || !hasRun || isBusy}><StepForward className="h-4 w-4 mr-1" />Step</Button>
          <Button size="sm" variant="outline" onClick={() => void pause()} disabled={!isRunning || isBusy}><Pause className="h-4 w-4 mr-1" />Pause</Button>
          <Button size="sm" variant="outline" onClick={() => void resume()} disabled={!isPaused || isBusy}><RotateCcw className="h-4 w-4 mr-1" />Resume</Button>
        </>
      )}
      <Button size="sm" variant="destructive" onClick={() => { if (confirm('Stop the run?')) void stop(); }} disabled={!hasRun || isBusy || canStart}><Square className="h-4 w-4 mr-1" />Stop</Button>
    </div>
  );
}
